import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  AfterViewInit,
  Output,
  ViewChild,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { environment } from '@root/src/environments/environment';

declare const L: any;

@Component({
  selector: 'app-polygon-edit-map',
  templateUrl: './polygon-edit-map.component.html',
  styleUrls: ['./polygon-edit-map.component.scss'],
  standalone: true,
})
export class PolygonEditMapComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() initialCoordinates: number[][] = [];
  @Input() isEditable: boolean = true;
  @Output() coordinateChange = new EventEmitter<number[][]>();

  @ViewChild('mapContainer') mapContainer!: ElementRef;

  private map: any;
  private polygonLayer: any;
  private osmLayer: any;
  private satelliteLayer: any;
  private lastEmittedCoords: string = '';

  constructor() {}

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initMap();
    }, 200);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialCoordinates'] && this.map && !changes['initialCoordinates'].firstChange) {
      const newCoordsStr = JSON.stringify(changes['initialCoordinates'].currentValue || []);

      if (this.lastEmittedCoords === newCoordsStr) {
        return;
      }

      this.redrawPolygon();
    }
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  public fitBounds(): void {
    if (this.map && this.polygonLayer) {
      const bounds = this.polygonLayer.getBounds();
      if (bounds.isValid()) {
        this.map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }

  public getCurrentCoordinates(): number[][] {
    if (!this.polygonLayer) {
      return this.initialCoordinates || [];
    }

    const rawLatLngs = this.polygonLayer.getLatLngs();
    let latLngArray: any[] = [];

    if (Array.isArray(rawLatLngs)) {
      if (rawLatLngs.length > 0 && Array.isArray(rawLatLngs[0])) {
        latLngArray = rawLatLngs[0];
      } else {
        latLngArray = rawLatLngs;
      }
    }

    return latLngArray.map((point: any) => [point.lat, point.lng]);
  }

  private initMap(): void {
    if (!this.mapContainer) return;

    this.osmLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap',
    });

    this.satelliteLayer = L.tileLayer('https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', {
      maxZoom: 20,
      subdomains: ['0', '1', '2', '3'],
      attribution: '© Google',
    });

    this.map = L.map(this.mapContainer.nativeElement, {
      center: [environment.map.lat, environment.map.lng],
      zoom: 12,
      layers: [this.satelliteLayer],
      zoomControl: true,
      attributionControl: false,
    });

    const baseMaps = {
      Satellite: this.satelliteLayer,
      Scheme: this.osmLayer,
    };

    L.control.layers(baseMaps).addTo(this.map);

    setTimeout(() => {
      this.map.invalidateSize();
      this.redrawPolygon();
    }, 300);
  }

  private redrawPolygon(): void {
    if (!this.map) return;

    if (this.polygonLayer) {
      this.map.removeLayer(this.polygonLayer);
    }

    if (this.initialCoordinates && this.initialCoordinates.length > 0) {
      this.polygonLayer = L.polygon(this.initialCoordinates, {
        color: '#3388ff',
        weight: 3,
        fillColor: '#3388ff',
        fillOpacity: 0.2,
      }).addTo(this.map);

      const bounds = this.polygonLayer.getBounds();
      if (bounds.isValid()) {
        this.map.fitBounds(bounds, { padding: [50, 50] });
      }

      if (this.isEditable) {
        this.enableEditing();
      }
    }
  }

  private enableEditing(): void {
    if (!this.polygonLayer) return;

    if (this.polygonLayer.pm) {
      this.polygonLayer.pm.disable();

      const pmOptions = {
        allowSelfIntersection: false,
        preventMarkerRemoval: false,
        draggable: true,
        snappable: true,
      };

      this.polygonLayer.pm.enable(pmOptions);

      this.polygonLayer.on('pm:edit', () => this.emitCurrentCoords());
      this.polygonLayer.on('pm:markerdragend', () => this.emitCurrentCoords());
      this.polygonLayer.on('pm:dragend', () => this.emitCurrentCoords());
    } else if (this.polygonLayer.editing) {
      this.polygonLayer.editing.enable();

      if (this.polygonLayer.editing._updateMarkers) {
        this.polygonLayer.editing._updateMarkers();
      }

      this.polygonLayer.on('edit', () => this.emitCurrentCoords());
    }
  }

  private emitCurrentCoords(): void {
    const cleanCoords = this.getCurrentCoordinates();
    this.lastEmittedCoords = JSON.stringify(cleanCoords);
    this.coordinateChange.emit(cleanCoords);
  }
}
